import React, { FC, Fragment, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Order, OperationButtonsConfig, OrderType } from 'models/order';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { getOrderDetailsOperationButtons } from 'components/order-details/utils/get-order-details-sub-nav-buttons';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import CustomerOperationSection from 'components/order-details/main-order-view/top-bar-operation-section/customer-operation-section/customer-operation-section.component';
import SupplierOperationSection from 'components/order-details/main-order-view/top-bar-operation-section/supplier-operation-section/supplier-operation-section.component';
import ArchiveOrderDialog from 'components/orders/order-details/archive-order-dialog.component';
import UnArchiveOrderDialog from 'components/orders/order-details/un-archive-order-dialog.component';
import ResendLabOrderByDentistDialog from 'components/order-details/main-order-view/top-bar-operation-section/resend-lab-order-by-dentist-dialog.component';
import { useNavigate } from 'react-router-dom';
import { isCustomer, isSupplier } from 'utils/client-settings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { ScannerCase } from 'models/scanner-case';

type TopBarOperationSectionProps = {
    currency: string;
    order: Order;
    onHandleDeliveryNoteCreation: () => void;
};
const TopBarOperationSection: FC<TopBarOperationSectionProps> = ({
    currency,
    order,
    onHandleDeliveryNoteCreation,
}: TopBarOperationSectionProps) => {
    const { t: translate } = useTranslation(['orders']);
    const navigate = useNavigate();
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const [showOperationButtons, setShowOperationButtons] = useState<OperationButtonsConfig>();
    const [openArchiveOrderDialog, setArchiveOrderDialog] = useState(false);
    const [openUnArchiveOrderDialog, setUnArchiveOrderDialog] = useState(false);
    const [openResendLabOrderByDentistDialog, setResendLabOrderByDentistDialog] = useState(false);

    useEffect(() => {
        setShowOperationButtons(getOrderDetailsOperationButtons(order));
    }, [order]);

    const redirectToModifyOrder = (isResend: boolean) => {
        if (isCustomer(currentOrganization._id, order.customer._id) && order.isCreatedByLab) {
            setResendLabOrderByDentistDialog(true);
            return;
        }
        if (order.isCreatedByLab) {
            const scanCase = order.scannerCase as ScannerCase;
            const resendString = isResend ? 'isResend=true' : '';
            const caseIdStr = scanCase?._id ? `caseId=${scanCase._id}` : '';
            const queryString = resendString && caseIdStr ? `${resendString}&${caseIdStr}` : resendString || caseIdStr;
            navigate(`/orders/modify-lab/${order._id}?${queryString}`);
        } else if (order.type === OrderType.SIMPLE_TYPE) {
            if (isResend) {
                navigate(`/orders/modify-simple/${order._id}?isResend=${isResend}`);
            } else {
                navigate(`/orders/modify-simple/${order._id}`);
            }
        } else {
            navigate(`/orders/modify-action/${order._id}?isResend=${isResend}`);
        }
    };

    const handleCloseDialog = () => {
        setArchiveOrderDialog(false);
        setUnArchiveOrderDialog(false);
        setResendLabOrderByDentistDialog(false);
    };

    return (
        <Fragment>
            {isCustomer(currentOrganization._id, order.customer._id) ? (
                <CustomerOperationSection
                    order={order}
                    showOperationButtons={showOperationButtons}
                    currency={currency}
                />
            ) : undefined}
            {isSupplier(currentOrganization._id, order.supplier._id) ? (
                <SupplierOperationSection
                    order={order}
                    showOperationButtons={showOperationButtons}
                    onHandleDeliveryNoteCreation={onHandleDeliveryNoteCreation}
                />
            ) : undefined}
            {!order.isImportedOrder && !permissions.isExternalTechnician(currentUser.role) ? (
                <Fragment>
                    {showOperationButtons?.archiveOrder ? (
                        <Button
                            onClick={() => setArchiveOrderDialog(true)}
                            sx={{ marginLeft: 2 }}
                            startIcon={<ArchiveIcon />}
                            data-test="top-bar-operation.archive-order-btn"
                        >
                            {translate('orders:ARCHIVE_ORDER')}
                        </Button>
                    ) : undefined}
                    {showOperationButtons?.unArchiveOrder ? (
                        <Button
                            onClick={() => setUnArchiveOrderDialog(true)}
                            sx={{ marginLeft: 2 }}
                            startIcon={<UnarchiveIcon />}
                            data-test="top-bar-operation.unarchive-order-btn"
                        >
                            {translate('orders:UNARCHIVE_ORDER')}
                        </Button>
                    ) : undefined}
                    {/* DD-3484: a sub-order is not resent by the lab that subcontracted it
                        (parentOrder resolves only for that lab); it subcontracts anew. */}
                    {showOperationButtons?.resendOrder && !order.parentOrder ? (
                        <Button
                            onClick={() => redirectToModifyOrder(true)}
                            sx={{ marginLeft: 2 }}
                            startIcon={<SettingsBackupRestoreIcon />}
                            data-test="top-bar-operation.resend-order-btn"
                        >
                            {translate('orders:RESEND_ORDER')}
                        </Button>
                    ) : undefined}
                </Fragment>
            ) : undefined}

            <ArchiveOrderDialog open={openArchiveOrderDialog} onClose={handleCloseDialog} orderId={order._id} />
            <UnArchiveOrderDialog open={openUnArchiveOrderDialog} onClose={handleCloseDialog} orderId={order._id} />
            <ResendLabOrderByDentistDialog
                open={openResendLabOrderByDentistDialog}
                order={order}
                onClose={handleCloseDialog}
            />
        </Fragment>
    );
};
export default TopBarOperationSection;
